import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "../lib/i18n";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SponsorsBanner from "../components/SponsorsBanner";
import {
  MapPin,
  Sparkles,
  Camera,
  Calendar,
  CreditCard,
  Star,
  ArrowRight,
  Check,
} from "lucide-react";

const COPY = {
  fr: {
    meta_title: "Pourquoi rejoindre CAFÉ-SPOT ? · Cafés, boulangeries, pâtisseries du Québec",
    meta_desc:
      "Faites découvrir votre commerce partout au Québec. CAFÉ-SPOT — la plateforme spécialisée pour cafés, boulangeries, pâtisseries et salons de thé. Standard 39 $ CAD / mois, Premium 59 $ CAD / mois.",
    overline: "Pour les commerces",
    title: "Pourquoi rejoindre",
    titleAccent: "CAFÉ-SPOT ?",
    subtitle: "Faites découvrir votre commerce partout au Québec.",
    intro:
      "Une plateforme québécoise pensée pour mettre en valeur le savoir-faire artisanal — pas un annuaire généraliste de plus.",
    ctaPrimary: "Inscrire mon commerce",
    ctaSecondary: "Voir les abonnements",

    sections: [
      {
        icon: MapPin,
        kicker: "Section 01 · Visibilité",
        title: "Visibilité partout au Québec",
        body:
          "CAFÉ-SPOT vous fait découvrir par les bonnes personnes, là où elles cherchent vraiment : un café près d'elles, une boulangerie artisanale, une pâtisserie d'exception, un salon de thé pour une pause.",
        bullets: [
          "Résidents du Québec à la recherche de leur prochaine adresse",
          "Touristes qui veulent goûter à l'artisanat local",
          "Visiteurs de passage en quête d'une vraie expérience",
          "Amateurs de cafés, pâtisseries et boulangeries",
        ],
        highlight: "Faites découvrir votre commerce partout au Québec.",
      },
      {
        icon: Sparkles,
        kicker: "Section 02 · Spécialisation",
        title: "Une plateforme spécialisée",
        body:
          "Contrairement aux annuaires généralistes qui mélangent des milliers de catégories, CAFÉ-SPOT est dédié à un seul univers : celui du goût artisanal.",
        bullets: [
          "Cafés indépendants et torréfacteurs",
          "Boulangeries artisanales",
          "Pâtisseries et chocolateries",
          "Salons de thé et maisons de dégustation",
        ],
      },
      {
        icon: Camera,
        kicker: "Section 03 · Présentation",
        title: "Présentez votre savoir-faire",
        body:
          "Une fiche commerce élégante, pensée pour mettre votre travail en valeur. Les visiteurs doivent avoir envie de pousser votre porte.",
        bullets: [
          "Logo et photo de façade",
          "Galerie photo professionnelle",
          "Menu, horaires, adresse, téléphone",
          "Services, ambiance, informations utiles",
        ],
        highlight: "Une présentation visuelle digne de votre commerce.",
      },
      {
        icon: Calendar,
        kicker: "Section 04 · Réservations",
        title: "Réservations optionnelles",
        body:
          "Chaque commerce choisit son mode de fonctionnement. Aucune obligation, vous activez ou désactivez les réservations en un clic depuis votre tableau de bord.",
        bullets: [
          "Avec réservations en ligne",
          "Ou sans réservations — visibilité seule",
          "Activable / désactivable à tout moment",
          "Demandes envoyées directement à votre courriel",
        ],
      },
    ],

    plansLabel: "Section 05 · Abonnements",
    plansTitle: "Abonnements simples",
    plansSub: "Sans engagement · annulable à tout moment · taxes en sus (TPS 5 % · TVQ 9,975 %)",
    plans: [
      {
        id: "standard",
        name: "Standard",
        price: "39",
        currency: "$ CAD / mois",
        features: [
          "Logo et photo de façade",
          "Jusqu'à 15 photos dans la galerie",
          "Menu, horaires, coordonnées",
          "Présence sur la carte interactive",
          "Statistiques de base",
          "Réservations optionnelles",
        ],
        cta: "Commencer",
        highlighted: false,
      },
      {
        id: "premium",
        name: "Premium",
        price: "59",
        currency: "$ CAD / mois",
        features: [
          "Tout le Standard",
          "Jusqu'à 30 photos dans la galerie",
          "Badge Premium",
          "Visibilité prioritaire dans les résultats",
          "Mise en avant sur la carte",
          "Apparition dans les commerces vedettes",
          "Statistiques avancées",
        ],
        cta: "Choisir Premium",
        highlighted: true,
      },
    ],

    diffLabel: "Section 06 · Différence",
    diffTitle: "Pourquoi CAFÉ-SPOT est différent",
    diffItems: [
      ["Plateforme spécialisée", "Pas un annuaire généraliste. Un univers, une expertise."],
      ["Recherche simplifiée", "Filtres par ville, catégorie, services — trouvé en quelques secondes."],
      ["Visibilité ciblée", "Vos clients potentiels sont exactement ceux qui visitent CAFÉ-SPOT."],
      ["Présentation professionnelle", "Une fiche élégante, soignée, qui valorise votre travail."],
      ["Découverte partout au Québec", "De Montréal aux régions, votre commerce devient visible."],
    ],

    ctaTitle: "Prêt à faire découvrir votre commerce ?",
    ctaSub:
      "Inscription en quelques minutes. Aucune carte requise pour créer votre compte — paiement uniquement au moment de l'activation.",
    ctaStart: "Commencer maintenant",
    ctaPlans: "Voir les abonnements",
  },

  en: {
    meta_title: "Why join CAFÉ-SPOT? · Quebec cafés, bakeries & pastry shops",
    meta_desc:
      "Get discovered across Quebec. CAFÉ-SPOT — the dedicated platform for cafés, bakeries, pastry shops and tea rooms. Standard $39 CAD / month, Premium $59 CAD / month.",
    overline: "For businesses",
    title: "Why join",
    titleAccent: "CAFÉ-SPOT?",
    subtitle: "Get your business discovered across Quebec.",
    intro:
      "A Quebec platform built to showcase artisan craftsmanship — not yet another generic directory.",
    ctaPrimary: "List my business",
    ctaSecondary: "See plans",

    sections: [
      {
        icon: MapPin,
        kicker: "Section 01 · Visibility",
        title: "Visibility across Quebec",
        body:
          "CAFÉ-SPOT puts you in front of the right people, exactly where they're looking : a café nearby, an artisan bakery, an exceptional pastry shop, a tea room for a break.",
        bullets: [
          "Quebec residents looking for their next spot",
          "Tourists wanting to taste local craftsmanship",
          "Visitors passing through seeking a real experience",
          "Coffee, pastry and bakery enthusiasts",
        ],
        highlight: "Get your business discovered across Quebec.",
      },
      {
        icon: Sparkles,
        kicker: "Section 02 · Focus",
        title: "A specialized platform",
        body:
          "Unlike generic directories mixing thousands of categories, CAFÉ-SPOT is dedicated to a single world : artisan taste.",
        bullets: [
          "Independent cafés and roasters",
          "Artisan bakeries",
          "Pastry shops and chocolatiers",
          "Tea rooms and tasting houses",
        ],
      },
      {
        icon: Camera,
        kicker: "Section 03 · Presentation",
        title: "Showcase your craft",
        body:
          "An elegant business page designed to highlight your work. Visitors should feel compelled to walk through your door.",
        bullets: [
          "Logo and façade photo",
          "Professional photo gallery",
          "Menu, hours, address, phone",
          "Services, atmosphere, useful info",
        ],
        highlight: "A visual presentation worthy of your business.",
      },
      {
        icon: Calendar,
        kicker: "Section 04 · Reservations",
        title: "Optional reservations",
        body:
          "Each business chooses its own model. No obligation — enable or disable reservations in one click from your dashboard.",
        bullets: [
          "With online reservations",
          "Or without — visibility only",
          "Toggle anytime",
          "Requests sent straight to your inbox",
        ],
      },
    ],

    plansLabel: "Section 05 · Plans",
    plansTitle: "Simple plans",
    plansSub: "No commitment · cancel anytime · taxes extra (GST 5 % · QST 9.975 %)",
    plans: [
      {
        id: "standard",
        name: "Standard",
        price: "39",
        currency: "$ CAD / month",
        features: [
          "Logo and façade photo",
          "Up to 15 gallery photos",
          "Menu, hours, contact",
          "Presence on interactive map",
          "Basic stats",
          "Optional reservations",
        ],
        cta: "Get started",
        highlighted: false,
      },
      {
        id: "premium",
        name: "Premium",
        price: "59",
        currency: "$ CAD / month",
        features: [
          "Everything in Standard",
          "Up to 30 gallery photos",
          "Premium badge",
          "Priority visibility",
          "Highlighted on map",
          "Featured businesses sections",
          "Advanced stats",
        ],
        cta: "Choose Premium",
        highlighted: true,
      },
    ],

    diffLabel: "Section 06 · Difference",
    diffTitle: "Why CAFÉ-SPOT is different",
    diffItems: [
      ["Specialized platform", "Not a generic directory. One world, one expertise."],
      ["Streamlined search", "Filters by city, category, services — found in seconds."],
      ["Targeted visibility", "Your potential customers are exactly the ones browsing CAFÉ-SPOT."],
      ["Professional presentation", "An elegant, polished page that values your work."],
      ["Discovery across Quebec", "From Montréal to the regions, your business gets seen."],
    ],

    ctaTitle: "Ready to get discovered?",
    ctaSub:
      "Sign up in minutes. No card required to create your account — payment only at activation.",
    ctaStart: "Get started now",
    ctaPlans: "See plans",
  },
};

export default function WhyCafeSpot() {
  const { lang } = useI18n();
  const c = COPY[lang] || COPY.fr;

  useEffect(() => {
    const prevTitle = document.title;
    document.title = c.meta_title;
    let meta = document.querySelector('meta[name="description"]');
    let created = false;
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
      created = true;
    }
    const prevDesc = meta.getAttribute("content") || "";
    meta.setAttribute("content", c.meta_desc);

    // Structured data (JSON-LD)
    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: c.meta_title,
      description: c.meta_desc,
      inLanguage: lang === "en" ? "en-CA" : "fr-CA",
      url: "https://cafe-spot.ca/why",
      publisher: { "@type": "Organization", name: "CAFÉ-SPOT", url: "https://cafe-spot.ca" },
    });
    document.head.appendChild(ld);

    return () => {
      document.title = prevTitle;
      if (created) meta.remove();
      else meta.setAttribute("content", prevDesc);
      ld.remove();
    };
  }, [lang, c.meta_title, c.meta_desc]);

  return (
    <div data-testid="why-page" className="min-h-screen text-[#F5F0EB]">
      <Header />

      {/* HERO */}
      <section className="relative pt-40 pb-24 px-6 md:px-12 overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_20%_0%,rgba(197,160,89,0.10),transparent_55%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_50%_at_90%_100%,rgba(93,64,55,0.18),transparent_60%)]" />
        </div>
        <div className="max-w-[1200px] mx-auto grid lg:grid-cols-[1.2fr_1fr] gap-16 items-end">
          <div>
            <div className="overline mb-6" data-testid="why-overline">{c.overline}</div>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-light leading-[0.95]">
              {c.title} <span className="text-[#C5A059] italic">{c.titleAccent}</span>
            </h1>
            <p className="mt-8 font-serif text-2xl md:text-3xl text-[#F5F0EB] max-w-2xl leading-snug">
              {c.subtitle}
            </p>
            <p className="mt-6 text-[#A8A39D] text-base max-w-xl leading-relaxed">{c.intro}</p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/register" data-testid="why-cta-register" className="btn-gold">
                {c.ctaPrimary} <ArrowRight size={14} />
              </Link>
              <a href="#plans" data-testid="why-cta-plans" className="btn-ghost">{c.ctaSecondary}</a>
            </div>
          </div>
          <div className="hidden lg:block relative">
            <div className="aspect-[4/5] bg-[#141210] border border-white/5 relative overflow-hidden">
              <div
                className="absolute inset-0 bg-cover bg-center grayscale-[15%]"
                style={{
                  backgroundImage:
                    "url(https://images.pexels.com/photos/302894/pexels-photo-302894.jpeg)",
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0908] via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 right-6">
                <div className="text-[10px] tracking-[0.35em] text-[#C5A059] uppercase mb-2">
                  cafe-spot.ca
                </div>
                <div className="font-serif text-2xl">Québec · Canada</div>
              </div>
            </div>
            <div className="absolute -bottom-6 -left-6 w-24 h-24 border border-[#C5A059]/30" />
          </div>
        </div>
      </section>

      {/* SECTIONS 1-4 */}
      <section className="px-6 md:px-12 py-20 border-t border-white/5">
        <div className="max-w-[1200px] mx-auto space-y-28">
          {c.sections.map((s, i) => {
            const Icon = s.icon;
            const reverse = i % 2 === 1;
            return (
              <div
                key={i}
                data-testid={`why-section-${i + 1}`}
                className={`grid md:grid-cols-[1fr_1.3fr] gap-12 md:gap-20 items-start ${
                  reverse ? "md:[&>:first-child]:order-2" : ""
                }`}
              >
                <div>
                  <div className="overline mb-5">{s.kicker}</div>
                  <div className="w-14 h-14 border border-[#C5A059]/30 flex items-center justify-center mb-6">
                    <Icon size={24} className="text-[#C5A059]" strokeWidth={1.2} />
                  </div>
                  <h2 className="font-serif text-4xl md:text-5xl font-light leading-tight">
                    {s.title}
                  </h2>
                </div>
                <div>
                  <p className="text-[#F5F0EB] text-base leading-7 max-w-[60ch]">{s.body}</p>
                  <ul className="mt-8 space-y-3">
                    {s.bullets.map((b, j) => (
                      <li
                        key={j}
                        className="flex items-start gap-3 text-[#A8A39D] text-sm leading-6"
                      >
                        <span className="text-[#C5A059] flex-shrink-0 mt-[6px] w-3 h-px bg-[#C5A059]" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                  {s.highlight && (
                    <div className="mt-8 pl-5 border-l-2 border-[#C5A059] font-serif italic text-xl md:text-2xl text-[#C5A059]">
                      « {s.highlight} »
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* PLANS */}
      <section id="plans" className="px-6 md:px-12 py-24 border-t border-white/5 bg-[#0E0C0B]">
        <div className="max-w-[1200px] mx-auto">
          <div className="text-center mb-16">
            <div className="overline mb-4 inline-flex items-center gap-2">
              <CreditCard size={12} /> {c.plansLabel}
            </div>
            <h2 className="font-serif text-4xl md:text-6xl font-light">{c.plansTitle}</h2>
            <p className="mt-4 text-[#A8A39D] text-sm max-w-xl mx-auto">{c.plansSub}</p>
          </div>
          <div className="grid md:grid-cols-2 gap-6 max-w-[900px] mx-auto">
            {c.plans.map((p) => (
              <div
                key={p.id}
                data-testid={`why-plan-${p.id}`}
                className={`relative bg-[#141210] border p-10 ${
                  p.highlighted ? "border-[#C5A059]/50" : "border-white/5"
                }`}
              >
                {p.highlighted && (
                  <div className="absolute top-0 right-6 -translate-y-1/2 bg-[#C5A059] text-[#0A0908] text-[10px] tracking-[0.3em] uppercase px-3 py-1 flex items-center gap-1.5">
                    <Star size={10} fill="currentColor" /> Premium
                  </div>
                )}
                <div className="overline mb-3">{p.name}</div>
                <div className="flex items-baseline gap-2 mb-8">
                  <div className="font-serif text-6xl text-[#C5A059] leading-none">{p.price}</div>
                  <div className="text-[#A8A39D] text-sm">{p.currency}</div>
                </div>
                <ul className="space-y-3 mb-10">
                  {p.features.map((f, j) => (
                    <li
                      key={j}
                      className="flex items-start gap-3 text-[#F5F0EB] text-sm leading-6"
                    >
                      <Check
                        size={14}
                        className="text-[#C5A059] flex-shrink-0 mt-1"
                        strokeWidth={1.5}
                      />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to={`/register?plan=${p.id}&role=cafe_owner`}
                  data-testid={`why-plan-cta-${p.id}`}
                  className={p.highlighted ? "btn-gold w-full justify-center" : "btn-ghost w-full justify-center"}
                >
                  {p.cta} <ArrowRight size={14} />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DIFFERENCE */}
      <section className="px-6 md:px-12 py-24 border-t border-white/5">
        <div className="max-w-[1200px] mx-auto">
          <div className="overline mb-4">{c.diffLabel}</div>
          <h2 className="font-serif text-4xl md:text-6xl font-light max-w-3xl">{c.diffTitle}</h2>
          <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5">
            {c.diffItems.map(([title, body], i) => (
              <div
                key={i}
                data-testid={`why-diff-${i}`}
                className="bg-[#0A0908] p-8 group hover:bg-[#141210] transition-colors duration-500"
              >
                <div className="text-[10px] tracking-[0.4em] text-[#C5A059] uppercase mb-4">
                  0{i + 1}
                </div>
                <h3 className="font-serif text-2xl mb-3 group-hover:text-[#C5A059] transition-colors">
                  {title}
                </h3>
                <p className="text-[#A8A39D] text-sm leading-6">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 md:px-12 py-32 border-t border-white/5 relative overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_70%_60%_at_50%_50%,rgba(197,160,89,0.12),transparent_60%)]" />
        <div className="max-w-[900px] mx-auto text-center">
          <div className="overline mb-6">cafe-spot.ca</div>
          <h2 className="font-serif text-5xl md:text-7xl font-light leading-[1]">
            {c.ctaTitle}
          </h2>
          <p className="mt-8 text-[#A8A39D] text-base max-w-xl mx-auto leading-relaxed">
            {c.ctaSub}
          </p>
          <div className="mt-12 flex flex-wrap gap-3 justify-center">
            <Link to="/register" data-testid="why-bottom-cta-register" className="btn-gold">
              {c.ctaStart} <ArrowRight size={14} />
            </Link>
            <Link to="/dashboard/billing" data-testid="why-bottom-cta-plans" className="btn-ghost">
              {c.ctaPlans}
            </Link>
          </div>
        </div>
      </section>

      <SponsorsBanner />
      <Footer />
    </div>
  );
}
